import { FileReader } from '@/platform'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import { buildDefaultVirtualResourceRoot } from '@/features/virtual-resource/rules'
import DataFileMap from '@/model/Resource/DataFileMap'
import { buildCurrentProjectResourceUrl } from '@/features/static-resource/resourceCdn'

export interface PublishedMapEntry {
  relativePath: string
  size: number
  md5: string
}

const MD5_RE = /^[a-f0-9]{32}$/i

function normalizePath(value: string): string {
  return String(value || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/')
    .trim()
}

function splitPath(value: string): string[] {
  return normalizePath(value).split('/').filter(Boolean)
}

function ensureFolder(parent: DataFileMap, name: string): DataFileMap {
  const found = parent.children.find((item) => item.isFolder && item.name === name)
  if (found) return found
  const folder = DataFileMap.categoryRoot(name)
  parent.children.push(folder)
  return folder
}

function addFile(root: DataFileMap, folderSegments: string[], fileName: string, entry: PublishedMapEntry): void {
  let cursor = root
  for (const segment of folderSegments) {
    cursor = ensureFolder(cursor, segment)
  }
  const existingByName = cursor.children.find((item) => !item.isFolder && item.name === fileName)
  if (existingByName) {
    existingByName.resourceUrl = buildCurrentProjectResourceUrl(entry.md5)
    existingByName.resourceMd5 = entry.md5
    existingByName.resourceSize = entry.size
    return
  }
  const leaf = DataFileMap.fileLeaf(fileName, buildCurrentProjectResourceUrl(entry.md5))
  leaf.resourceMd5 = entry.md5
  leaf.resourceSize = entry.size
  cursor.children.push(leaf)
}

function getGraphicTopFolder(pathSegments: string[]): { top: string; rest: string[] } {
  const first = pathSegments[1]?.toLowerCase() || ''
  switch (first) {
    case 'button':
      return { top: '按钮', rest: pathSegments.slice(2) }
    case 'background':
      return { top: '背景', rest: pathSegments.slice(2) }
    case 'oafs':
      return { top: '动图', rest: pathSegments.slice(2) }
    case 'half':
      return { top: '立绘', rest: pathSegments.slice(2) }
    case 'chat':
      return { top: '气泡', rest: pathSegments.slice(2) }
    case 'ui':
      return { top: 'UI', rest: pathSegments.slice(2) }
    case 'face':
      return { top: '头像', rest: pathSegments.slice(2) }
    case 'system':
      return { top: '系统', rest: pathSegments.slice(2) }
    case 'other':
      return { top: '其他', rest: pathSegments.slice(2) }
    default:
      return { top: '其他', rest: pathSegments.slice(1) }
  }
}

function getAudioTopFolder(pathSegments: string[]): { top: string; rest: string[] } {
  const first = pathSegments[1]?.toLowerCase() || ''
  switch (first) {
    case 'bgm':
      return { top: '音乐', rest: pathSegments.slice(2) }
    case 'se':
      return { top: '音效', rest: pathSegments.slice(2) }
    case 'bgs':
      return { top: '循环音效', rest: pathSegments.slice(2) }
    case 'voice':
      return { top: '语音', rest: pathSegments.slice(2) }
    default:
      return { top: '音效', rest: pathSegments.slice(1) }
  }
}

function isBitmapFontFile(fileName: string): boolean {
  return /\.(png|xfi|list)$/i.test(fileName)
}

export function parsePublishedMapManifest(bytes: Uint8Array): PublishedMapEntry[] {
  const reader = new FileReader(bytes)
  const count = reader.readInt()
  const entries: PublishedMapEntry[] = []
  if (!Number.isFinite(count) || count < 0 || count > 100000) {
    throw new Error('Map.bin 条目数量异常')
  }
  for (let index = 0; index < count; index += 1) {
    const relativePath = normalizePath(reader.readString())
    const size = reader.readInt()
    const md5 = reader.readString().trim().toLowerCase()
    if (!relativePath || !MD5_RE.test(md5)) {
      continue
    }
    entries.push({ relativePath, size, md5 })
  }
  return entries
}

export function findMapEntry(entries: PublishedMapEntry[], relativePath: string): PublishedMapEntry | null {
  const target = normalizePath(relativePath).toLowerCase()
  return entries.find((entry) => normalizePath(entry.relativePath).toLowerCase() === target) ?? null
}

export function buildVirtualResourcesFromMap(
  entries: PublishedMapEntry[],
  options?: {
    includeBitmapFonts?: boolean
  },
): Record<ResourceCategoryKey, DataFileMap | null> {
  const roots: Record<ResourceCategoryKey, DataFileMap | null> = {
    graphic: buildDefaultVirtualResourceRoot('graphic'),
    audio: buildDefaultVirtualResourceRoot('audio'),
    font: buildDefaultVirtualResourceRoot('font'),
    video: buildDefaultVirtualResourceRoot('video'),
  }

  for (const entry of entries) {
    const segments = splitPath(entry.relativePath)
    const fileName = segments[segments.length - 1] || ''
    if (!fileName || !entry.md5) {
      continue
    }
    const head = segments[0]?.toLowerCase() || ''
    if (head === 'graphics' && segments[1]?.toLowerCase() === 'video') {
      addFile(roots.video!, ['视频', ...segments.slice(2, -1)], fileName, entry)
      continue
    }
    if (head === 'graphics') {
      const folder = getGraphicTopFolder(segments)
      addFile(roots.graphic!, [folder.top, ...folder.rest.slice(0, -1)], fileName, entry)
      continue
    }
    if (head === 'audio') {
      const folder = getAudioTopFolder(segments)
      addFile(roots.audio!, [folder.top, ...folder.rest.slice(0, -1)], fileName, entry)
      continue
    }
    if (head === 'font') {
      if (!options?.includeBitmapFonts && isBitmapFontFile(fileName)) {
        continue
      }
      addFile(roots.font!, [isBitmapFontFile(fileName) ? '位图' : '文件', ...segments.slice(1, -1)], fileName, entry)
      continue
    }
  }

  return roots
}
